import { Section } from 'components/section'
import { motion } from 'framer-motion'
import styled, { css } from 'styled-components/macro'

import { styleReset } from 'styles/misc'
import { mq } from 'styles/mq'
import { theme } from 'styles/theme'
import { typography } from 'styles/typography'

export const StyledSection = styled(Section)`
  --max-map-mobile-width: 20rem;
  --map-width: 100%;

  overflow: hidden;
  margin-block-end: 0;
  padding-block-end: var(--spacing-lg);

  ${mq.ltLarge} {
    --map-width: min(
      calc(var(--max-map-mobile-width) - var(--spacing-sm)),
      calc(50% - 2 * var(--spacing-sm))
    );
  }
`

export const ListContainer = styled.div`
  position: relative;
  padding-block: var(--spacing-lg);
  margin-block: calc(-1 * var(--spacing-lg) + var(--spacing-sm))
    calc(-1 * var(--spacing-lg));
  z-index: 0;
`

export const List = styled.ul`
  display: flex;
  padding: 0;
  margin: 0;
  gap: var(--spacing-sm);

  ${mq.ltLarge} {
    flex-direction: column;
  }
`

export const Map = styled.img`
  display: block;
  margin-inline: auto;
  width: 100%;
  aspect-ratio: 2 / 1;

  ${mq.ltLarge} {
    [data-reversed='false'] & {
      width: var(--map-width);
      margin: 0;
      position: absolute;
    }
  }
`

export const Item = styled(motion.li)`
  position: relative;
  margin: 0;
  list-style: none;
  font-size: 1.5rem;
  text-align: left;
  transform-origin: 50% 50%;
  perspective: 60rem;

  ${mq.large} {
    flex-grow: 1;
    width: 0;
    perspective: 100vw;
  }
`

export const Content = styled(motion.div)`
  ${theme.shadow()};
  padding: var(--spacing-sm) var(--spacing-xsm);
  border-radius: 3rem;
  background-color: ${theme.colors.secondary};

  ${mq.ltLarge} {
    overflow: hidden;
    box-sizing: content-box;

    &::after {
      content: '';
      display: block;
      position: absolute;
      left: 0;
      bottom: 0;
      width: 100%;
      height: 5rem;
      border-bottom-left-radius: 3rem;
      border-bottom-right-radius: 3rem;
      pointer-events: none;
      background: linear-gradient(
        to top,
        ${theme.colors.secondary},
        transparent
      );
    }
  }

  &[data-reversed='true'] {
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: var(--spacing-md);
  }
`

export const Country = styled.h3`
  ${typography.h3}
  position: relative;
  margin: var(--spacing-sm) 0 1rem;

  ${mq.large} {
    [data-reversed='false'] & {
      font-size: 2.4rem;
    }
  }

  ${mq.ltLarge} {
    [data-reversed='false'] & {
      margin-top: 0;
      padding-left: min(var(--max-map-mobile-width), 50%);
    }
  }
`

export const Description = styled.p`
  ${typography.boxSmall};
  position: relative;
  margin: 0 0 1rem;
  overflow: hidden;

  ${mq.ltLarge} {
    [data-reversed='false'] & {
      padding-left: min(var(--max-map-mobile-width), 50%);
    }
  }

  ${mq.large} {
    [data-reversed='false'] & {
      mask: linear-gradient(to top, transparent, black 50%);
      height: 14rem;
    }
  }
`

export const ReadMore = styled.button`
  ${styleReset}
  position: absolute;
  top: 0;

  &::before {
    content: '';
    display: block;
    aspect-ratio: 1;
  }

  [data-reversed='false'] & {
    left: 0;
    display: flex;
    align-items: end;
    width: 100%;
    height: 100%;
    cursor: pointer;

    ${mq.large} {
      justify-content: center;
    }

    &::before {
      width: 2.8rem;
      margin-bottom: 1rem;
      transition: transform 0.3s;
      background: url("data:image/svg+xml,%3Csvg width='28' height='28' viewBox='0 0 28 28' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M14 21.4947L6.98834 14.483L8.63801 12.8322L14.0047 18.1988L19.3713 12.8322L21.0117 14.483L14.0012 21.4947H14ZM14 15.1667L6.98834 8.15382L8.63801 6.50415L14.0047 11.8708L19.3713 6.50415L21.0117 8.15382L14.0012 15.1655L14 15.1667Z' fill='%23102C44'/%3E%3C/svg%3E%0A");
      z-index: 1;
    }

    &:hover::before,
    &:focus-visible::before {
      transform: translateY(10%);
    }
  }

  [data-reversed='true'] & {
    margin: var(--spacing-sm);
    right: 0;
    display: block;
    width: min-content;
    height: min-content;
    cursor: pointer;

    &::before {
      width: 2.4rem;
      mask: url("data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M12 22.0002C9.34711 22.0026 6.80218 20.9498 4.9263 19.0739C3.05042 17.198 1.99762 14.6531 2 12.0002V11.8002C2.08179 7.79241 4.5478 4.22035 8.26637 2.72325C11.9849 1.22616 16.2381 2.09308 19.074 4.92619C21.9365 7.78627 22.7932 12.0895 21.2443 15.8278C19.6955 19.5661 16.0465 22.0026 12 22.0002ZM12 13.4102L14.59 16.0002L16 14.5902L13.41 12.0002L16 9.41019L14.59 8.00019L12 10.5902L9.41001 8.00019L8.00001 9.41019L10.59 12.0002L8.00001 14.5902L9.41001 16.0002L12 13.4112V13.4102Z' fill='%23A0157F'/%3E%3C/svg%3E%0A");
      background: currentColor;
    }

    &:hover::before,
    &:focus-visible::before {
      transform: rotate(90deg);
    }
  }

  ${mq.ltLarge} {
    [data-reversed='false'] &::before {
      margin-left: calc((var(--map-width) + var(--spacing-sm)) / 2);
    }
  }
`

export const Backdrop = styled.div.attrs({ role: 'button', tabIndex: 0 })<{
  $isVisible: boolean
}>`
  display: block;
  position: absolute;
  inset: var(--spacing-sm) -2rem;
  opacity: 0;
  pointer-events: none;
  transition: 0.3s all;
  background-color: rgba(255 255 255 / 50%);
  z-index: 5;

  ${(props) =>
    props.$isVisible &&
    css`
      opacity: 1;
      pointer-events: auto;
      backdrop-filter: blur(5px);
      cursor: pointer;
    `}
`

export const Reverse = styled.div`
  display: flex;
  position: absolute;
  left: 50%;
  top: 0;
  width: 100%;
  transform: translateX(-50%) rotateY(180deg);
  backface-visibility: hidden;
  transform-style: preserve-3d;

  ${mq.ltLarge} {
    ${Item}:first-child & {
      top: 0;
    }

    ${Item} ~ ${Item}:not(:last-child) & {
      transform: translate(-50%, calc(-50% + 7rem)) rotateY(180deg);
    }

    ${Item}:last-child & {
      top: auto;
      bottom: 0;
    }
  }

  ${mq.large} {
    height: 100%;
    width: calc(300% + 2 * var(--spacing-sm));
  }
`
